<template>
  <Modal
    :isOpen="isOpen"
    @close="handleClose"
    :size="size"
    :closable="closable"
  >
    <div class="text-center">
      <!-- Icon -->
      <div v-if="showIcon" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4" :class="iconClasses">
        <Icon :name="getIconNameFromPath(iconPath)" size="lg" />
      </div>

      <!-- Title -->
      <Heading v-if="title" level="h3" class="mb-2">
        {{ title }}
      </Heading>

      <!-- Description -->
      <div v-if="description" class="mb-6">
        <Text 
          v-for="(sentence, index) in descriptionSentences" 
          :key="index" 
          variant="body-sm" 
          color="muted" 
          class="block"
        >
          {{ sentence }}
        </Text>
      </div>

      <!-- Custom content slot -->
      <div v-if="$slots.default" class="mb-6">
        <slot />
      </div>

      <!-- Details -->
      <div v-if="details && details.length > 0" class="mb-6 space-y-1">
        <Heading v-if="showDetailsTitle" level="h4" class="mb-2">{{ t('confirmationDialog.detailsLabel') }}</Heading>
        <div v-for="(detail, index) in details" :key="index" class="flex items-start">
          <Text v-if="!hideDetailsBullet" variant="body-sm" :color="detailsColor" class="mr-2">•</Text>
          <Text variant="body-sm" :color="detailsColor" class="block">{{ detail }}</Text>
        </div>
      </div>

      <!-- Warning text -->
      <div v-if="warningText" class="mb-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
        <Text variant="body-sm" class="text-yellow-800 dark:text-yellow-200">{{ warningText }}</Text>
      </div>

      <!-- Input field for confirmation -->
      <div v-if="requireInput" class="mb-6">
        <Text as="label" variant="label" class="block mb-2">
          {{ inputLabel }}
        </Text>
        <Input
          v-model="inputValue"
          :placeholder="inputPlaceholder"
          :type="inputType"
          :required="true"
          class="w-full"
        />
        <Text v-if="inputHelper" variant="caption" color="muted" class="mt-1">
          {{ inputHelper }}
        </Text>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="flex justify-end space-x-3">
        <Button
          v-if="showCancel"
          @click="handleCancel"
          :variant="cancelVariant"
          :disabled="loading"
        >
          {{ cancelText }}
        </Button>
        <Button
          @click="handleConfirm"
          :variant="confirmVariant"
          :loading="loading"
          :disabled="!canConfirm"
        >
          {{ confirmText }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Modal from './Modal.vue'
import Button from './Button.vue'
import Input from './Input.vue'
import Heading from './Heading.vue'
import Text from './Text.vue'
import Icon from './Icon.vue'
import { ICONS } from '@/constants/icons'
import { CONFIRMATION_ICON_COLORS } from '@/constants/uiColors'
import { t } from '@/i18n/en-us'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: () => t('confirmationDialog.title')
  },
  description: {
    type: String,
    default: ''
  },
  details: {
    type: Array,
    default: () => []
  },
  warningText: {
    type: String,
    default: null
  },
  confirmText: {
    type: String,
    default: () => t('confirmationDialog.confirm')
  },
  cancelText: {
    type: String,
    default: () => t('confirmationDialog.cancel')
  },
  confirmVariant: {
    type: String,
    default: 'danger',
    validator: (value) => ['primary', 'secondary', 'danger', 'success', 'warning'].includes(value)
  },
  cancelVariant: {
    type: String,
    default: 'secondary',
    validator: (value) => ['primary', 'secondary', 'danger', 'success', 'warning'].includes(value)
  },
  iconType: {
    type: String,
    default: 'warning',
    validator: (value) => ['warning', 'danger', 'info', 'success', 'question'].includes(value)
  },
  showIcon: {
    type: Boolean,
    default: true
  },
  showCancel: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
  },
  closable: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  requireInput: {
    type: Boolean,
    default: false
  },
  inputLabel: {
    type: String,
    default: 'Type to confirm'
  },
  inputPlaceholder: {
    type: String,
    default: 'Enter confirmation text'
  },
  inputType: {
    type: String,
    default: 'text'
  },
  inputHelper: {
    type: String,
    default: null
  },
  expectedInput: {
    type: String,
    default: null
  },
  showDetailsTitle: {
    type: Boolean,
    default: true
  },
  hideDetailsBullet: {
    type: Boolean,
    default: false
  },
  detailsColor: {
    type: String,
    default: 'muted',
    validator: (value) => ['default', 'muted', 'primary', 'secondary', 'success', 'warning', 'danger', 'info'].includes(value)
  },
  customIcon: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['confirm', 'cancel', 'close'])

// State
const inputValue = ref('')

// Computed
const iconClasses = computed(() => {
  const base = 'mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4'
  const colorClasses = CONFIRMATION_ICON_COLORS[props.iconType]?.combined || CONFIRMATION_ICON_COLORS.question.combined
  return `${base} ${colorClasses}`
})

const iconPath = computed(() => {
  // Use custom icon if provided
  if (props.customIcon) {
    return ICONS[props.customIcon] || props.customIcon
  }
  
  // Otherwise use iconType mapping
  const iconMap = {
    warning: ICONS.warning,
    danger: ICONS.warning, // danger uses same icon as warning
    info: ICONS.info,
    success: ICONS.success,
    question: ICONS.question
  }
  return iconMap[props.iconType] || ICONS.question
})

const descriptionSentences = computed(() => {
  if (!props.description) return []
  // Split by sentence endings (. ! ?)
  return props.description
    .split(/(?<=[.!?])\s+/)
    .filter(s => s.trim().length > 0)
    .map(s => s.trim())
})

const canConfirm = computed(() => {
  if (!props.requireInput) return true
  if (!props.expectedInput) return inputValue.value.trim().length > 0
  return inputValue.value.trim() === props.expectedInput
})

// Methods
const handleConfirm = () => {
  if (!canConfirm.value) return
  
  emit('confirm', {
    inputValue: inputValue.value
  })
}

const handleCancel = () => {
  emit('cancel')
  reset()
}

const handleClose = () => {
  emit('close')
  reset()
}

const reset = () => {
  inputValue.value = ''
}

// Helper function to extract icon name from ICONS constant path
const getIconNameFromPath = (iconPath) => {
  if (!iconPath) return ''
  
  for (const [name, path] of Object.entries(ICONS)) {
    if (path === iconPath) {
      return name
    }
  }
  
  return ''
}

// Watch for modal close to reset input
watch(() => props.isOpen, (newValue) => {
  if (!newValue) {
    reset()
  }
})
</script>
